/**
 * 打印机模块 - 枚举定义
 * 
 * @module types/printer/enums
 * @description 打印机类型、状态等枚举
 * @backend-file backend/app/Modules/Printer/Models/Printer.php
 * @author YSFL Team
 * @date 2025-11-03
 */

/**
 * 打印机类型枚举
 */
export enum PrinterType {
  /** 蓝牙 */
  BLUETOOTH = 1,
  /** WiFi */
  WIFI = 2,
}

/**
 * 打印机状态枚举
 */
export enum PrinterStatus {
  /** 在线 */
  ONLINE = 1,
  /** 离线 */
  OFFLINE = 2,
  /** 故障 */
  ERROR = 3,
  /** 禁用 */
  DISABLED = 4,
}

/**
 * 打印模板类型枚举
 * 
 * 说明：
 * - 采购收货使用 MATERIAL_LABEL（物料标签）
 * - 销售发货使用 PRODUCT_LABEL（产品标签）
 */
export enum PrintTemplateType {
  /** 入库单 */
  WAREHOUSE_IN = 1,
  /** 出库单 */
  WAREHOUSE_OUT = 2,
  /** 物料标签（也用于采购收货） */
  MATERIAL_LABEL = 3,
  /** 产品标签（也用于销售发货） */
  PRODUCT_LABEL = 4,
  /** 工单标签 */
  WORK_ORDER_LABEL = 5,
  /** 设备标签 */
  EQUIPMENT_LABEL = 6,
  /** 工装标签 */
  TOOLING_LABEL = 7,
  /** 质检单 */
  INSPECTION_LABEL = 8,
  /** 货位标签 */
  LOCATION_LABEL = 9,
  /** 物料货位标签 */
  MATERIAL_LOCATION_LABEL = 10,
  /** NCR单 */
  NCR_ORDER = 11,
  /** 返工单 */
  REWORK_ORDER = 12,
  /** 报废单 */
  SCRAP_ORDER = 13,
  /** 其他 */
  OTHER = 99,
}

/**
 * 打印模板类型选项（用于下拉选择）
 * 统一维护，Web和App共用
 */
export const PRINT_TEMPLATE_TYPE_OPTIONS = [
  { value: PrintTemplateType.WAREHOUSE_IN, label: '入库单' },
  { value: PrintTemplateType.WAREHOUSE_OUT, label: '出库单' },
  { value: PrintTemplateType.MATERIAL_LABEL, label: '物料标签' },
  { value: PrintTemplateType.PRODUCT_LABEL, label: '产品标签' },
  { value: PrintTemplateType.WORK_ORDER_LABEL, label: '工单标签' },
  { value: PrintTemplateType.EQUIPMENT_LABEL, label: '设备标签' },
  { value: PrintTemplateType.TOOLING_LABEL, label: '工装标签' },
  { value: PrintTemplateType.INSPECTION_LABEL, label: '质检单' },
  { value: PrintTemplateType.LOCATION_LABEL, label: '货位标签' },
  { value: PrintTemplateType.MATERIAL_LOCATION_LABEL, label: '物料货位标签' },
  { value: PrintTemplateType.NCR_ORDER, label: 'NCR单' },
  { value: PrintTemplateType.REWORK_ORDER, label: '返工单' },
  { value: PrintTemplateType.SCRAP_ORDER, label: '报废单' },
  { value: PrintTemplateType.OTHER, label: '其他' },
] as const

/**
 * 根据模板类型获取标签文本
 */
export function getPrintTemplateTypeLabel(type: PrintTemplateType): string {
  const option = PRINT_TEMPLATE_TYPE_OPTIONS.find(opt => opt.value === type)
  return option?.label ?? '未知'
}

/**
 * 标签类型枚举（对应SDK）
 */
export enum LabelType {
  /** 间隙纸 */
  GAP = 1,
  /** 黑标纸 */
  BLACK_MARK = 2,
  /** 连续纸 */
  CONTINUOUS = 3,
  /** 定孔纸 */
  HOLE = 4,
  /** 透明纸 */
  TRANSPARENT = 5,
}

/**
 * 打印模式枚举（对应SDK）
 */
export enum PrintMode {
  /** 热敏 */
  THERMAL = 1,
  /** 热转印 */
  THERMAL_TRANSFER = 2,
}

/**
 * 打印模板状态枚举
 */
export enum PrintTemplateStatus {
  /** 启用 */
  ENABLED = 1,
  /** 禁用 */
  DISABLED = 2,
}

/**
 * 打印日志状态枚举
 */
export enum PrintLogStatus {
  /** 成功 */
  SUCCESS = 1,
  /** 失败 */
  FAILED = 2,
  /** 取消 */
  CANCELLED = 3,
}

/**
 * 打印机绑定类型枚举
 */
export enum PrinterBindingType {
  /** 角色 */
  ROLE = 1,
  /** 用户 */
  USER = 2,
  /** 工位 */
  WORKSTATION = 3,
  /** 部门 */
  DEPARTMENT = 4,
}
